'use client';

import { useEffect, useRef } from 'react';
import { useReactFlow } from '@xyflow/react';
import { useWorkspaceActions } from './store';
import { looksLikeUrl } from './clipboard-handler';
import { lookupIframeEmbed, type IframeEmbed } from '@/lib/content/iframe-embeds';

type AddItem = ReturnType<typeof useWorkspaceActions>['addItem'];

/**
 * Default canvas size for an iframe-embed widget. Aspect ratio comes
 * from the registry (16:9 for videos, 4:3 for code sandboxes, etc.);
 * we pick a base width and derive height. Larger than the OG-card
 * default (360×260) because embeds are interactive surfaces — small
 * sizes are illegible for video / code playgrounds.
 */
function embedDefaultSize(embed: IframeEmbed): { width: number; height: number } {
  const width = 560;
  const height = Math.round(width / (embed.aspectRatio ?? 16 / 9));
  return { width, height };
}

/**
 * POST a URL to the widget import endpoint (Plan v3-05 Tier A/B).
 * Returns the new content id + post id, or null if the import was
 * denied (X-Frame-Options / SSRF / feature flag).
 *
 * Exported so the LinkAnnotation "Embed" button can reuse the exact
 * same import pipeline as the paste handler.
 */
export async function importUrlAsExternalWidget(url: string): Promise<
  { widgetContentId: string; postId: string } | null
> {
  try {
    const res = await fetch('/api/widgets/import-from-url', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ url }),
    });
    if (!res.ok) return null;
    const data = (await res.json().catch(() => null)) as
      | { ok: boolean; widgetContentId?: string; postId?: string }
      | null;
    if (!data?.ok || !data.widgetContentId || !data.postId) return null;
    return { widgetContentId: data.widgetContentId, postId: data.postId };
  } catch {
    return null;
  }
}

/**
 * Try to spawn an iframe-embed widget for a URL. Returns `true` if a
 * widget was created + added to the canvas, `false` if the URL isn't
 * iframe-embeddable (caller should fall through to OG-card preview).
 *
 * Strategy:
 *   1. Look up the URL in the embed registry. If matched, we use the
 *      transformer's output (e.g. `youtube.com/watch?v=X` becomes
 *      `youtube.com/embed/X`) — this is what makes "paste YouTube
 *      URL → playable video tile" work.
 *   2. POST the transformed URL to `/api/widgets/import-from-url`.
 *      The server runs the SSRF / X-Frame-Options gates again and
 *      persists an `external-passive` (or `-cooperative`) widget.
 *   3. Add a `content` workspace item pointing at the new widget
 *      content id at the cursor anchor.
 */
async function trySpawnEmbedWidget(
  rawUrl: string,
  point: { x: number; y: number },
  addItem: AddItem,
): Promise<boolean> {
  const embed = lookupIframeEmbed(rawUrl);
  if (!embed) return false;
  const imported = await importUrlAsExternalWidget(embed.embedUrl);
  if (!imported) return false;
  const size = embedDefaultSize(embed);
  await addItem({
    type: 'content',
    refId: imported.widgetContentId,
    placement: 'cursor',
    state: {
      x: point.x - size.width / 2,
      y: point.y - size.height / 2,
      ...size,
      cache: {
        displayName: embed.siteName,
        contentType: 'widget',
      },
    },
  }).catch(() => {});
  return true;
}

/**
 * Shared "drop an image / video file into the canvas as a content
 * widget" pipeline. Used by both the global paste handler (Cmd+V
 * from the system clipboard) and the workspace drop handler (drag
 * a file from Finder / Files onto the canvas) — keeping the upload
 * + addItem flow in one place guarantees the two entry points behave
 * identically (same default size, same `hideHeader` policy, same
 * cache priming).
 *
 * Returns `true` on success so callers know to consume the gesture,
 * `false` when the upload failed (network / quota / permission). The
 * caller is responsible for anchoring `point` in flow coordinates and
 * for screening out non-media files before calling.
 */
export async function uploadFileAsContentItem(
  file: File,
  point: { x: number; y: number },
  addItem: AddItem,
): Promise<boolean> {
  const fd = new FormData();
  fd.append('file', file);
  fd.append('visibility', 'unlisted');
  const res = await fetch('/api/content/upload', {
    method: 'POST',
    body: fd,
  });
  if (!res.ok) return false;
  const data = await res.json().catch(() => null);
  const id = data?.content?.id ?? data?.id;
  if (!id) return false;
  // The server classifies the upload (image / video / audio / document / code / …).
  // Media renders in the body, so it goes headerless + media-sized. Any OTHER file
  // (PDF, code, zip, .robproj, …) keeps its title bar (name + type) at a doc size.
  const ct: string =
    (data?.content?.contentType as string | undefined) ??
    (/^video\//.test(file.type) ? 'video' : /^image\//.test(file.type) ? 'image' : /^audio\//.test(file.type) ? 'audio' : 'document');
  const isMedia = ct === 'image' || ct === 'video';
  const baseSize =
    ct === 'video'
      ? { width: 480, height: 320 }
      : ct === 'image'
        ? { width: 360, height: 360 }
        : ct === 'audio'
          ? { width: 340, height: 130 }
          : { width: 340, height: 240 };
  await addItem({
    type: 'content',
    refId: id,
    // Cursor-anchored: pastes / drops happen at a known pointer
    // location. `placement: 'cursor'` keeps the widget near that
    // location and only slides it off if the exact pixel collides
    // with an existing widget.
    placement: 'cursor',
    state: {
      x: point.x - baseSize.width / 2,
      y: point.y - baseSize.height / 2,
      ...baseSize,
      // Media has its identity in the body (the image / video itself), so the
      // chrome title is noise — hide it (re-enable via right-click "Show widget
      // title"). Non-media files keep the header so the filename stays visible.
      hideHeader: isMedia,
      cache: {
        displayName: data?.content?.displayName ?? file.name,
        contentType: ct,
        mimeType: file.type || (data?.content?.mimeType as string | undefined) || 'application/octet-stream',
      },
    },
  }).catch(() => {});
  return true;
}

/**
 * Global workspace paste handler.
 *
 * Mounted by `WorkspaceShell` underneath `ReactFlowProvider` so it can
 * use `useReactFlow().screenToFlowPosition` to translate the pasted
 * content's anchor point into flow coordinates.
 *
 * Behaviour:
 *   1. Bail out if the user is focused inside an editable surface
 *      (input / textarea / contenteditable / CodeMirror) — that's
 *      where they want the paste to land naturally.
 *   2. If the clipboard contains an `image/*` or `video/*` file,
 *      upload via the existing `/api/content/upload` endpoint and
 *      drop a `content` widget at the anchor point.
 *   3. Otherwise, if there's non-empty plain text, drop a sticky
 *      annotation containing that text.
 *
 * Anchor point: last pointermove over the canvas (`lastPointer`).
 * Falls back to the centre of the visible viewport when nothing's
 * been hovered (e.g. immediately after switching tabs to a fresh
 * workspace and hitting Cmd+V before the cursor moves).
 */
export function PasteHandler() {
  const flow = useReactFlow();
  const { addItem } = useWorkspaceActions();
  const lastPointer = useRef<{ x: number; y: number } | null>(null);

  // Cache the most recent pointer position over the document so we can
  // anchor pastes at the cursor instead of a random corner. Listening
  // at the document level — bubbling pointer events from the canvas,
  // panels, chrome, etc. — gives us a stable last-known position even
  // when the user paste-triggers from a chrome button.
  useEffect(() => {
    const onMove = (e: PointerEvent) => {
      lastPointer.current = { x: e.clientX, y: e.clientY };
    };
    document.addEventListener('pointermove', onMove, { passive: true });
    return () => document.removeEventListener('pointermove', onMove);
  }, []);

  useEffect(() => {
    const onPaste = (e: ClipboardEvent) => {
      // Skip when focus is inside an editable surface — the user is
      // typing into a chat input / code editor / sticky note and
      // expects the paste to land there, not on the canvas.
      const active = document.activeElement as HTMLElement | null;
      if (active) {
        const tag = active.tagName;
        if (tag === 'INPUT' || tag === 'TEXTAREA') return;
        if (active.isContentEditable) return;
        // CodeMirror surface is a div with a contenteditable child;
        // walk up looking for any contentEditable ancestor.
        let n: HTMLElement | null = active;
        while (n) {
          if (n.isContentEditable) return;
          n = n.parentElement;
        }
      }

      const cd = e.clipboardData;
      if (!cd) return;

      // Anchor: cursor position if we have one, else viewport centre
      // in flow coords (use the wrapper midpoint so the new item lands
      // somewhere actually visible).
      const point = lastPointer.current
        ? flow.screenToFlowPosition({
            x: lastPointer.current.x,
            y: lastPointer.current.y,
          })
        : flow.screenToFlowPosition({
            x: window.innerWidth / 2,
            y: window.innerHeight / 2,
          });

      // 1) Image / video file → upload + content widget.
      const file = Array.from(cd.files ?? []).find((f) =>
        /^(image|video)\//.test(f.type),
      );
      if (file) {
        e.preventDefault();
        void uploadFileAsContentItem(file, point, addItem);
        return;
      }

      // 2) Plain text → either a link-preview card (when the text is
      //    a single URL) or a sticky annotation. The URL branch lets
      //    users grab any link from Slack / email / a browser tab and
      //    drop it into the workspace as a rich card without having
      //    to manually configure anything. The OG fetch happens on
      //    the annotation node itself (see `LinkAnnotation` in
      //    `components/workspace/nodes/annotation-node.tsx`); we
      //    spawn the row with `linkStatus: 'pending'` so the node
      //    can show a skeleton card during the round-trip.
      const text = cd.getData('text/plain');
      if (text && text.trim().length > 0) {
        e.preventDefault();
        if (looksLikeUrl(text)) {
          const url = text.trim();
          // Fast path: known iframe-embeddable host (YouTube, PlayCanvas,
          // CodePen, Figma, …). Spawn an `iframe-external` widget
          // instead of an OG link-card so the user gets a live,
          // interactive surface instead of a static preview tile.
          //
          // Falls back to the OG-card path on:
          //   - URL not in `iframe-embeds.ts` registry
          //   - import-from-url server denies (SSRF / X-Frame-Options
          //     / feature flag off)
          // …because the link card is always a useful default and we
          // never want a pasted URL to silently vanish.
          void (async () => {
            const ok = await trySpawnEmbedWidget(url, point, addItem);
            if (ok) return;
            const baseSize = { width: 360, height: 260 };
            await addItem({
              type: 'annotation',
              refId: null,
              placement: 'cursor',
              state: {
                x: point.x - baseSize.width / 2,
                y: point.y - baseSize.height / 2,
                ...baseSize,
                cache: {
                  kind: 'link',
                  url,
                  linkStatus: 'pending',
                },
              },
            }).catch(() => {});
          })();
          return;
        }
        const baseSize = { width: 240, height: 160 };
        void addItem({
          type: 'annotation',
          refId: null,
          // Cursor-anchored: sticky lands where the user was
          // pointing, only sliding off if the spot is already taken.
          placement: 'cursor',
          state: {
            x: point.x - baseSize.width / 2,
            y: point.y - baseSize.height / 2,
            ...baseSize,
            cache: {
              kind: 'sticky',
              text,
              color: 'yellow',
            },
          },
        }).catch(() => {});
      }
    };

    document.addEventListener('paste', onPaste);
    return () => document.removeEventListener('paste', onPaste);
  }, [addItem, flow]);

  return null;
}
